<script lang="ts">
	// The design system's NavItem (design/components/core/NavItem.jsx): one row
	// of a rail. The active row moves away from its ground: on the paper rail it
	// lifts to the card; inside a card it takes the emphasis surface.
	//
	// aria-current is not decoration: interaction.css keys the hover exclusion
	// off it, so styling and semantics stay one attribute, as in the design.
	//
	// The icon takes the row's colour, so the active, hover and card grounds
	// carry it with no rule of its own. The design composes it in as a child;
	// here it is named with `icon`.

	import Icon, { type IconName } from './Icon.svelte';
	import { pressable } from './pressable';

	interface Props {
		testId: string;
		label: string;
		onPress?: () => void;
		/** A row that goes somewhere is a link, so it works and can be followed without a script. */
		href?: string;
		/** A link that leaves for another site, in a new tab. */
		newTab?: boolean;
		active?: boolean;
		ground?: 'paper' | 'card';
		/** An 18px glyph before the label, in the row's colour. It replaces the dot. */
		icon?: IconName;
		/** A 7px signal dot, as a colour token. */
		dot?: string;
		/** A short count or tag on the trailing edge, in mono. */
		meta?: string;
	}

	let {
		testId,
		label,
		onPress,
		href,
		newTab = false,
		active = false,
		ground = 'paper',
		icon,
		dot,
		meta,
	}: Props = $props();
</script>

{#snippet row()}
	<span class="label" class:has-icon={!!icon}>
		{#if icon}
			<span class="icon" aria-hidden="true"><Icon name={icon} size={18} /></span>
		{:else if dot}
			<span class="dot" style:background={dot}></span>
		{/if}
		{label}
	</span>
	{#if meta}
		<span class="meta">{meta}</span>
	{/if}
{/snippet}

{#if href}
	<a
		{href}
		target={newTab ? '_blank' : undefined}
		rel={newTab ? 'noopener' : undefined}
		class="nav {ground}"
		class:active
		data-testid={testId}
		aria-current={active ? 'page' : undefined}
		data-nav
		data-press
		data-ground={active && ground === 'card' ? 'invert' : undefined}
		onclick={onPress}
		{@attach pressable}
	>
		{@render row()}
	</a>
{:else}
	<button
		type="button"
		class="nav {ground}"
		class:active
		data-testid={testId}
		aria-current={active ? 'page' : undefined}
		data-nav
		data-press
		data-ground={active && ground === 'card' ? 'invert' : undefined}
		onclick={onPress}
		{@attach pressable}
	>
		{@render row()}
	</button>
{/if}

<style>
	.nav {
		box-sizing: border-box;
		text-decoration: none;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		inline-size: 100%;
		text-align: start;
		min-block-size: var(--ctl-min);
		padding: 10px 12px;
		font-family: var(--font-sans);
		font-size: 14.5px;
		font-weight: 600;
		border: none;
		cursor: pointer;
		border-radius: var(--radius-input);
		background: transparent;
		color: var(--text-body);
	}

	.label {
		display: flex;
		align-items: center;
		gap: 9px;
	}

	.label.has-icon {
		gap: 10px;
	}

	.icon {
		display: grid;
		flex: none;
	}

	.dot {
		inline-size: 7px;
		block-size: 7px;
		border-radius: var(--radius-pill);
		flex: none;
	}

	.meta {
		font-family: var(--font-mono);
		font-size: 11.5px;
		color: var(--text-muted);
		flex: none;
	}

	.paper.active {
		background: var(--surface-card);
		color: var(--text-strong);
	}

	.card.active {
		background: var(--surface-invert);
		color: var(--on-ink);
	}

	.card.active .meta {
		color: var(--on-ink-muted);
	}
</style>
